import { DragDropProvider } from '@dnd-kit/react';
import { move } from '@dnd-kit/helpers';
import { U21Drawer, U21NoData, U21Spacer } from 'app/shared/u21-ui/components';
import { useState } from 'react';

import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { FilterContext, SavedFilter } from 'app/modules/filters/models';
import { ManageSavedFiltersRow } from 'app/modules/filters/components/ManageSavedFiltersRow';

interface Props {
  filterContext: FilterContext;
  onClose: () => void;
  onReorder: (orderedIds: number[]) => void;
  open: boolean;
  orderedSavedFilters: SavedFilter[];
  staleSavedFilterIds: Set<number>;
}

export const ManageSavedFiltersDrawer = ({
  filterContext,
  onClose,
  onReorder,
  open,
  orderedSavedFilters,
  staleSavedFilterIds,
}: Props) => {
  const [target, setTarget] = useState<SavedFilter | undefined>();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <U21Drawer
        onClose={onClose}
        open={open}
        size="small"
        title="Manage Saved Filters"
        variant="temporary"
      >
        {orderedSavedFilters.length === 0 ? (
          <U21NoData>No saved filters yet.</U21NoData>
        ) : (
          <DragDropProvider
            onDragEnd={(event) => {
              if (event.canceled) {
                return;
              }
              const reordered = move(orderedSavedFilters, event);
              const ids = reordered.map((i) => i.id);
              const original = orderedSavedFilters.map((i) => i.id);
              if (ids.every((id, i) => id === original[i])) {
                return;
              }
              onReorder(ids);
            }}
          >
            <U21Spacer>
              {orderedSavedFilters.map((i, index) => (
                <ManageSavedFiltersRow
                  key={i.id}
                  index={index}
                  isStale={staleSavedFilterIds.has(i.id)}
                  onDelete={(sf) => {
                    setTarget(sf);
                    setDeleteOpen(true);
                  }}
                  onEdit={(sf) => {
                    setTarget(sf);
                    setEditOpen(true);
                  }}
                  savedFilter={i}
                />
              ))}
            </U21Spacer>
          </DragDropProvider>
        )}
      </U21Drawer>
      {target && (
        <>
          <EditSavedFilterModal
            filters={target.filters}
            onClose={() => setEditOpen(false)}
            open={editOpen}
            savedFilter={target}
          />
          <DeleteSavedFilterModal
            filterContext={filterContext}
            onClose={() => setDeleteOpen(false)}
            open={deleteOpen}
            savedFilter={target}
          />
        </>
      )}
    </>
  );
};
